import { useState } from "react";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { AlertCircle, Trash2 } from "lucide-react";

export default function DeleteAccount() {
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [understood, setUnderstood] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDeleteAccount = async () => {
    setIsDeleting(true);
    // TODO: Implement actual account deletion logic
    console.log("Deleting account...");
    // Simulate deletion
    setTimeout(() => {
      setIsDeleting(false);
      setShowConfirmDialog(false);
    }, 2000);
  };

  return (
    <Layout>
      <div className="max-w-2xl mx-auto py-12 px-4">
        <div className="mb-8">
          <h1 className="text-4xl font-bold mb-2 text-white">Delete Account</h1>
          <p className="text-gray-300">Permanently delete your ShiftBear account and all associated data</p>
        </div>

        <div className="space-y-6 text-white">
          {/* Warning Card */}
          <Card className="border-destructive/50 bg-destructive/5">
            <CardHeader>
              <div className="flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-destructive mt-0.5 flex-shrink-0" />
                <div>
                  <CardTitle className="text-destructive">This action cannot be undone</CardTitle>
                  <CardDescription className="mt-1">
                    Deleting your account is permanent and irreversible. Please read the information below carefully.
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
          </Card>

          {/* What Gets Deleted */}
          <Card>
            <CardHeader>
              <CardTitle>What will be deleted</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="space-y-2">
                <div className="flex items-start gap-3">
                  <Trash2 className="h-5 w-5 text-muted-foreground mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-medium">Your account information</p>
                    <p className="text-sm text-muted-foreground">Email, password, and profile data</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Trash2 className="h-5 w-5 text-muted-foreground mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-medium">All your shift data</p>
                    <p className="text-sm text-muted-foreground">Work schedules, shift times, and notes</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Trash2 className="h-5 w-5 text-muted-foreground mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-medium">Team sharing data</p>
                    <p className="text-sm text-muted-foreground">Any shared schedules or team information</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Trash2 className="h-5 w-5 text-muted-foreground mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-medium">All backups</p>
                    <p className="text-sm text-muted-foreground">Backup copies will be deleted within 30 days</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* What Won't Be Deleted */}
          <Card>
            <CardHeader>
              <CardTitle>What won't be deleted</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground mb-3">
                The following information may persist in our systems:
              </p>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li>• Anonymized usage analytics (cannot be linked to you)</li>
                <li>• Logs required for security and legal compliance</li>
                <li>• Data shared with other users (they will retain their copies)</li>
              </ul>
            </CardContent>
          </Card>

          {/* Confirmation Checkbox */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Confirm deletion</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-start space-x-3">
                <Checkbox
                  id="understand"
                  checked={understood}
                  onCheckedChange={(checked) => setUnderstood(checked as boolean)}
                  className="mt-1"
                />
                <label
                  htmlFor="understand"
                  className="text-sm cursor-pointer leading-relaxed"
                >
                  I understand that deleting my account will permanently remove all my data and this action cannot be undone.
                </label>
              </div>
            </CardContent>
          </Card>

          {/* Delete Button */}
          <div className="flex gap-3">
            <Button
              variant="destructive"
              size="lg"
              disabled={!understood}
              onClick={() => setShowConfirmDialog(true)}
              className="w-full"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete My Account
            </Button>
          </div>

          {/* Alternative Contact */}
          <Card className="bg-muted/50">
            <CardHeader>
              <CardTitle className="text-base">Need help?</CardTitle>
            </CardHeader>
            <CardContent className="text-sm text-muted-foreground">
              <p className="mb-2">
                If you have questions or need assistance, please contact us at:
              </p>
              <a href="mailto:support@shiftbear.com" className="text-primary hover:underline font-medium">
                support@shiftbear.com
              </a>
            </CardContent>
          </Card>
        </div>

        {/* Confirmation Dialog */}
        <AlertDialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete account permanently?</AlertDialogTitle>
              <AlertDialogDescription>
                This will permanently delete your ShiftBear account and all associated data. This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <div className="bg-destructive/10 border border-destructive/20 rounded-md p-3 my-4">
              <p className="text-sm font-medium text-destructive">
                ⚠️ All your data will be permanently deleted within 30 days.
              </p>
            </div>
            <div className="flex gap-3">
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDeleteAccount}
                disabled={isDeleting}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                {isDeleting ? "Deleting..." : "Delete Account"}
              </AlertDialogAction>
            </div>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </Layout>
  );
}
